import { renderThumbnails, executeUntilFrame } from "./rewind-thumbnail.js";
import { RewindBuffer } from "../rewind.js";
import { RewindCaptureInterval } from "./emulation-loop.js";

const RewindBufferSnapshots = 30;

/**
 * Rewind scrubber UI — a filmstrip overlay showing thumbnails of recent
 * emulator states, allowing the user to click to restore any of them.
 */
export class RewindUI {
    /**
     * @param {object} options
     * @param {object} options.processor - Cpu6502 instance
     * @param {object} options.video - Video instance
     * @param {object} options.loop - the emulation loop: says when to capture, and is held while the panel is open
     */
    constructor({ processor, video, loop }) {
        this.rewindBuffer = new RewindBuffer(RewindBufferSnapshots);
        this.processor = processor;
        this.video = video;
        this.loop = loop;
        loop.addEventListener("rewind-capture", () => this.capture());

        this.panel = document.getElementById("rewind-panel");
        this.filmstrip = document.getElementById("rewind-filmstrip");
        this.closeBtn = document.getElementById("rewind-close");
        this.openBtn = document.getElementById("rewind-open");

        this.isOpen = false;
        this.resumeLoop = null;
        this.selectedIndex = -1;
        this.snapshots = [];
        this.savedState = null;

        this._onKeyDown = this._onKeyDown.bind(this);
        this.closeBtn.addEventListener("click", () => this.close());
        this.openBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this.open();
        });
    }

    capture() {
        this.rewindBuffer.push(this.processor.snapshotState());
        this.updateButtonState();
    }

    /** Forget everything captured, as on a hard reset. */
    reset() {
        this.close();
        this.rewindBuffer.clear();
        this.updateButtonState();
    }

    /** Open the rewind scrubber panel. */
    open() {
        if (this.isOpen) return;

        this.snapshots = this.rewindBuffer.getAll();
        if (this.snapshots.length === 0) return;

        this.resumeLoop = this.loop.pause("the rewind panel");
        this.isOpen = true;
        try {
            this.savedState = this.processor.snapshotState();
            const thumbnails = renderThumbnails(
                this.processor,
                this.snapshots,
                this.video,
                RewindCaptureInterval,
                this.savedState,
            );
            this._populateFilmstrip(thumbnails);
            this.panel.hidden = false;
            // Use capture phase so keys don't leak to the emulator's keyboard handler
            document.addEventListener("keydown", this._onKeyDown, true);

            // Select the newest snapshot ("now") and jump to it
            this.selectedIndex = this.snapshots.length - 1;
            this._restoreAndPaint(this.selectedIndex);
            this._updateSelectionHighlight();
            this.filmstrip.scrollLeft = this.filmstrip.scrollWidth;
        } catch (e) {
            if (this.savedState) this.processor.restoreState(this.savedState);
            this._closePanel();
            throw e;
        }
    }

    /**
     * Close the rewind panel, committing the selected snapshot.
     * The emulator resumes from the exact snapshot state.
     */
    commit() {
        if (!this.isOpen) return;
        // Re-restore the snapshot so the CPU resumes from the exact point,
        // not from the state advanced by executeUntilFrame during preview.
        if (this.selectedIndex >= 0 && this.selectedIndex < this.snapshots.length) {
            this.processor.restoreState(this.snapshots[this.selectedIndex]);
        }
        this._closePanel();
    }

    /**
     * Close the rewind panel, restoring the state from before it was opened.
     */
    cancel() {
        if (!this.isOpen) return;
        this._renderState(this.savedState);
        this._closePanel();
    }

    /** Alias for cancel — closing the panel without explicit commit cancels. */
    close() {
        this.cancel();
    }

    /**
     * Restore the emulator to the snapshot at the given index and update
     * both the main display and the filmstrip selection highlight.
     * @param {number} index - index into the snapshots array
     */
    selectSnapshot(index) {
        if (index < 0 || index >= this.snapshots.length) return;

        this.selectedIndex = index;
        this._restoreAndPaint(index);
        this._updateSelectionHighlight();
        this._scrollToSelected();
    }

    /** Update the disabled state of the Rewind menu item. */
    updateButtonState() {
        if (this.openBtn) {
            this.openBtn.classList.toggle("disabled", this.rewindBuffer.length === 0);
        }
    }

    _closePanel() {
        this.isOpen = false;
        this.panel.hidden = true;
        this.filmstrip.innerHTML = "";
        this.snapshots = [];
        this.savedState = null;
        document.removeEventListener("keydown", this._onKeyDown, true);
        this.resumeLoop?.();
        this.resumeLoop = null;
    }

    /**
     * Render a snapshot's frame to the display without advancing CPU state.
     * Restores the snapshot, runs to vsync for a complete frame, paints it,
     * then re-restores the snapshot so the CPU stays at the exact point.
     */
    _restoreAndPaint(index) {
        this._renderState(this.snapshots[index]);
    }

    /** Render a state to the display, restoring it afterwards. */
    _renderState(state) {
        this.processor.restoreState(state);
        executeUntilFrame(this.processor, this.video);
        this.video.paint();
        this.processor.restoreState(state);
    }

    _updateSelectionHighlight() {
        const thumbs = this.filmstrip.querySelectorAll(".rewind-thumb");
        for (const thumb of thumbs) {
            thumb.classList.toggle("selected", Number(thumb.dataset.index) === this.selectedIndex);
        }
    }

    _scrollToSelected() {
        const selected = this.filmstrip.querySelector(".rewind-thumb.selected");
        if (selected) {
            selected.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
        }
    }

    _populateFilmstrip(thumbnails) {
        this.filmstrip.innerHTML = "";
        for (const { canvas, index, ageSeconds } of thumbnails) {
            const wrapper = document.createElement("div");
            wrapper.className = "rewind-thumb";
            wrapper.dataset.index = index;
            wrapper.appendChild(canvas);

            const label = document.createElement("span");
            label.className = "rewind-thumb-label";
            label.textContent = ageSeconds === 0 ? "now" : `-${ageSeconds}s`;
            wrapper.appendChild(label);

            wrapper.addEventListener("click", () => this.selectSnapshot(index));
            this.filmstrip.appendChild(wrapper);
        }
    }

    _onKeyDown(e) {
        switch (e.key) {
            case "Escape":
                e.preventDefault();
                e.stopPropagation();
                this.cancel();
                break;
            case "Enter":
                e.preventDefault();
                e.stopPropagation();
                this.commit();
                break;
            case "ArrowLeft":
                e.preventDefault();
                e.stopPropagation();
                if (this.selectedIndex > 0) {
                    this.selectSnapshot(this.selectedIndex - 1);
                }
                break;
            case "ArrowRight":
                e.preventDefault();
                e.stopPropagation();
                if (this.selectedIndex < this.snapshots.length - 1) {
                    this.selectSnapshot(this.selectedIndex + 1);
                }
                break;
            default:
                // Block keys from reaching the emulator but allow browser
                // shortcuts (Tab, accessibility keys, etc.) to work normally
                e.stopPropagation();
                break;
        }
    }
}
